import SearchIcon from '@mui/icons-material/Search';
import { Box, Dialog, FormHelperText, IconButton, InputBase, Paper, useMediaQuery, useTheme } from '@mui/material';
import { IconClose } from 'assets/icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import TooltipCC from './TooltipCC';

export const SearchCC = ({
    value,
    setValue,
    handleValue,
    backgroundColor = '#ffff',
    sx,
    placeholder = 'subtitles.input.placeholder',
    maxLength = 1000,
    marginLeft,
    height,
    heightMobile,
    sxWrapper,
    ...props
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [openSearchDialog, setOpenSearchDialog] = useState(false);

    const renderSearchBox = (isMobileView) => (
        <Paper
            className="sub-search-wrapper"
            component="form"
            sx={{
                p: '0px',
                display: 'flex',
                alignItems: 'center',
                width: isMobileView ? '100%' : { xl: '300px' },
                height: isMobileView ? (heightMobile || '44px') : (height || '36px'),
                backgroundColor: backgroundColor,
                boxShadow: 'none',
                position: 'relative',
                ...(!isMobileView && sx),
            }}
        >
            <InputBase
                className={`sub-search-box ${value?.length > maxLength ? 'sub-search-box-error' : ''}`}
                sx={{
                    flex: 1,
                    color: value?.length > 0 ? 'auto' : '#8F9294',
                    ml: isMobileView ? 1 : 0,
                    ...(isMobileView && {
                        '& input': {
                            border: 'none !important'
                        },
                        '&:hover input': {
                            border: 'none !important'
                        },
                        '&.Mui-focused input': {
                            border: 'none !important'
                        }
                    })
                }}
                placeholder={t(placeholder)}
                error={true}
                inputProps={{ 'aria-label': 'search ' }}
                value={value}
                autoFocus={isMobileView}
                onChange={(e) => {
                    if (e.target.value?.length > 0) {
                        setValue(e.target.value);
                    } else {
                        handleValue('');
                        setValue(e.target.value);
                    }
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        if (value?.length <= maxLength) {
                            setValue(value.trim());
                            handleValue(value.trim());
                            if (isMobileView) setOpenSearchDialog(false);
                        }
                    }
                }}
                onBlur={() => setValue(value.trim())}
            />
            {value?.length > 0 && (
                <TooltipCC title={t('actions.clearInput')} placement="bottom-start">
                    <IconButton
                        type="button"
                        tabIndex={0}
                        sx={{ p: 0.5, position: 'absolute', right: isMobileView ? '50px' : '40px' }}
                        onClick={() => {
                            handleValue('');
                            setValue('');
                        }}
                    >
                        <IconClose />
                    </IconButton>
                </TooltipCC>
            )}
            <IconButton
                type="button"
                aria-label="search"
                sx={(theme) => ({
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: '0 4px 4px 0',
                    padding: isMobileView ? '0 12px' : '0 6px',
                    height: '100%',
                    '&:hover': {
                        backgroundColor: theme.palette.primary.main,
                        boxShadow: `rgba(0, 0, 0, 0.2) 0px 2px 4px -1px, rgba(0, 0, 0, 0.14) 0px 4px 5px 0px, rgba(0, 0, 0, 0.12) 0px 1px 10px 0px`
                    }
                })}
                onClick={() => {
                    if (value?.length <= maxLength) {
                        handleValue(value.trim());
                        if (isMobileView) setOpenSearchDialog(false);
                    }
                }}
                id={`btn-${props?.id}${isMobileView ? '-mobile' : ''}`}
            >
                <SearchIcon sx={{ color: '#fff' }} />
            </IconButton>
        </Paper>
    );

    if (isMobile) {
        return (
            <Box sx={{ marginLeft: marginLeft, ...sx, position: 'relative' }} {...props}>
                <IconButton
                    type="button"
                    onClick={() => setOpenSearchDialog(true)}
                    sx={(theme) => ({
                        backgroundColor: theme.palette.primary.main,
                        borderRadius: '6px',
                        padding: {xs: '8.25px 12px', sm: '6.25px 12px'},
                        color: '#fff',
                        '&:hover': {
                            backgroundColor: theme.palette.primary.main,
                            boxShadow: `rgba(0, 0, 0, 0.2) 0px 2px 4px -1px, rgba(0, 0, 0, 0.14) 0px 4px 5px 0px, rgba(0, 0, 0, 0.12) 0px 1px 10px 0px`
                        }
                    })}
                >
                    <SearchIcon />
                </IconButton>
                <Dialog
                    open={openSearchDialog}
                    onClose={() => setOpenSearchDialog(false)}
                    fullWidth
                    maxWidth="sm"
                    sx={{
                        '& .MuiDialog-paper': {
                            margin: '16px',
                            width: 'calc(100% - 32px)',
                            position: 'absolute',
                            top: '8px'
                        }
                    }}
                >
                    {renderSearchBox(true)}
                    {value?.length > maxLength && (
                        <FormHelperText sx={{ color: 'rgb(211, 47, 47)', position: 'absolute', bottom: '-20px', left: '16px' }}>
                            {t('validate.max-n-length', { num: maxLength })}
                        </FormHelperText>
                    )}
                </Dialog>
            </Box>
        );
    }

    return (
        <Box sx={{ marginLeft: marginLeft, position: 'relative', ...sxWrapper }} {...props}>
            {renderSearchBox(false)}
            {value?.length > maxLength && (
                <FormHelperText sx={{ color: 'rgb(211, 47, 47)', position: 'absolute' }}>
                    {t('validate.max-n-length', { num: maxLength })}
                </FormHelperText>
            )}
        </Box>
    );
};
